const finiteNumber = (value) =>
  value === null ||
  value === undefined ||
  value === "" ||
  !Number.isFinite(Number(value))
    ? null
    : Number(value);
export const formatRecentGameDate = (value) => {
  if (!value) return "–";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleDateString("de-DE");
};

export const prepareRecentGameScore = (score) => {
  if (Array.isArray(score)) {
    const current = score.filter(
      (item) =>
        !item?.description ||
        String(item.description).toUpperCase() === "CURRENT"
    );
    const value = (side) =>
      Number(
        current.find(
          (item) =>
            String(
              item?.score?.participant ?? item?.participant
            ).toLowerCase() === side
        )?.score?.goals
      );
    const home = value("home");
    const away = value("away");
    return Number.isFinite(home) && Number.isFinite(away)
      ? `${home}:${away}`
      : "–";
  }
  const home = Number(score?.home);
  const away = Number(score?.away);
  return Number.isFinite(home) && Number.isFinite(away)
    ? `${home}:${away}`
    : "–";
};

export const prepareRecentGameDetail = (item, teamId) => {
  const fixture = item?.fixture || item;
  const participants = Array.isArray(fixture?.participants)
    ? fixture.participants
    : [];
  const participantAt = (location) =>
    participants.find(
      (participant) =>
        String(participant?.meta?.location || "").toLowerCase() === location
    );
  const homeTeam = participantAt("home");
  const awayTeam = participantAt("away");
  const homeName =
    fixture?.home_team?.name ||
    fixture?.localTeam?.name ||
    fixture?.localteam?.name ||
    homeTeam?.name ||
    "–";
  const awayName =
    fixture?.away_team?.name ||
    fixture?.visitorTeam?.name ||
    fixture?.visitorteam?.name ||
    awayTeam?.name ||
    "–";
  const homeId = Number(
    fixture?.home_team?.id ??
      fixture?.localTeam?.id ??
      fixture?.localteam_id ??
      homeTeam?.id
  );
  const awayId = Number(
    fixture?.away_team?.id ??
      fixture?.visitorTeam?.id ??
      fixture?.visitorteam_id ??
      awayTeam?.id
  );
  const venue = Number(teamId) === homeId ? "Heim" : "Auswärts";
  const startingAt = item?.starting_at || fixture?.starting_at;
  return {
    fixtureId: Number(item?.fixture_id ?? fixture?.id),
    form: String(item?.form || "").toUpperCase(),
    homeName,
    awayName,
    opponent: venue === "Heim" ? awayName : homeName,
    venue,
    teamId: Number(teamId),
    homeId,
    awayId,
    score: prepareRecentGameScore(
      item?.score || fixture?.score || fixture?.scores
    ),
    startingAt,
    date: formatRecentGameDate(startingAt),
  };
};

export const calculateFixturePerformanceIndex = ({
  realDifference,
  expectedDifference,
  shouldDifference,
}) => {
  const actual = finiteNumber(realDifference);
  const expected = finiteNumber(expectedDifference);
  const should = finiteNumber(shouldDifference);
  if (actual === null || expected === null || should === null) return null;
  return should - expected + (actual - expected) / 3;
};

export const buildRecentPerformance = (
  games,
  expectedLedger = [],
  shouldLedger = []
) => {
  const expectedByFixture = new Map(
    expectedLedger.map((entry) => [Number(entry?.fixture_id), entry])
  );
  const shouldByFixture = new Map(
    shouldLedger.map((entry) => [Number(entry?.fixture_id), entry])
  );
  return (Array.isArray(games) ? games : []).map((game) => {
    const expected = expectedByFixture.get(Number(game?.fixtureId));
    const should = shouldByFixture.get(Number(game?.fixtureId));
    const [homeGoals, awayGoals] = String(game?.score || "")
      .split(":")
      .map(Number);
    const isHome = Number(game?.teamId) === Number(game?.homeId);
    const orient = (home, away) => (isHome ? home - away : away - home);
    const realDifference =
      Number.isFinite(homeGoals) && Number.isFinite(awayGoals)
        ? orient(homeGoals, awayGoals)
        : null;
    const orientedDifference = (home, away) => {
      const homeValue = finiteNumber(home);
      const awayValue = finiteNumber(away);
      return homeValue === null || awayValue === null
        ? null
        : orient(homeValue, awayValue);
    };
    const expectedDifference = expected
      ? orientedDifference(expected.homegoalexp, expected.awaygoalexp)
      : null;
    const shouldDifference = should
      ? orientedDifference(should.should_goals_home, should.should_goals_away)
      : null;
    return {
      ...game,
      goals: {
        real: {
          for: realDifference === null ? null : finiteNumber(isHome ? homeGoals : awayGoals),
          against: realDifference === null ? null : finiteNumber(isHome ? awayGoals : homeGoals),
        },
        expected: {
          for: finiteNumber(
            isHome ? expected?.homegoalexp : expected?.awaygoalexp
          ),
          against: finiteNumber(
            isHome ? expected?.awaygoalexp : expected?.homegoalexp
          ),
        },
        should: {
          for: finiteNumber(
            isHome ? should?.should_goals_home : should?.should_goals_away
          ),
          against: finiteNumber(
            isHome ? should?.should_goals_away : should?.should_goals_home
          ),
        },
      },
      performanceIndex: calculateFixturePerformanceIndex({
        realDifference,
        expectedDifference,
        shouldDifference,
      }),
    };
  });
};

export const selectRecentGames = (games, limit) =>
  (Array.isArray(games) ? games : [])
    .slice()
    .sort((a, b) => new Date(b.startingAt || 0) - new Date(a.startingAt || 0))
    .slice(0, limit);
